import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import { Hand, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'waybig_user_vote';

const flavorOptions = [
  { id: 'coffee', name: 'Coffee', emoji: '☕' },
  { id: 'matcha', name: 'Matcha', emoji: '🍵' },
  { id: 'cheesecake', name: 'Cheesecake', emoji: '🍰' },
  { id: 'red-velvet', name: 'Red Velvet', emoji: '❤️' },
];

const VoteSection = () => {
  const [votes, setVotes] = useState({ coffee: 0, matcha: 0, cheesecake: 0, 'red-velvet': 0 });
  const [total, setTotal] = useState(0);
  const [userVote, setUserVote] = useState(null);
  const [voting, setVoting] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    fetchVotes();
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setUserVote(saved);
  }, []);

  const fetchVotes = async () => {
    try {
      const res = await axios.get(`${API}/votes`);
      setVotes(res.data.votes);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Fetch votes failed', e);
    }
  };

  const handleVote = async (flavorId) => {
    if (userVote || voting) return;
    setVoting(true);
    try {
      await axios.post(`${API}/votes/${flavorId}`);
      localStorage.setItem(STORAGE_KEY, flavorId);
      setUserVote(flavorId);
      await fetchVotes();
    } catch (e) {
      console.error('Vote failed', e);
    } finally {
      setVoting(false);
    }
  };

  const getPercent = (count) => total === 0 ? 0 : Math.round((count / total) * 100);
  const maxCount = Math.max(...Object.values(votes));

  return (
    <section ref={ref} data-testid="vote-section" className="py-24 px-6 bg-[#FFB300] relative overflow-hidden">
      <div className="absolute -top-10 -right-10 font-['Bebas_Neue'] text-[200px] text-black/5 leading-none select-none pointer-events-none">
        VOTE
      </div>
      <div className="absolute -bottom-10 -left-10 font-['Bebas_Neue'] text-[200px] text-black/5 leading-none select-none pointer-events-none">
        VOTE
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          {/* Live indicator */}
          <div className="inline-flex items-center gap-2 bg-black px-4 py-2 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3D00] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3D00]"></span>
            </span>
            <span className="font-['Outfit'] font-bold uppercase text-xs tracking-[0.25em] text-[#FFB300]">
              Live · {total.toLocaleString()} votes counted
            </span>
          </div>

          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl text-black uppercase leading-none mb-4" data-testid="vote-heading">
            Vote for the<br /><span className="text-[#FF3D00]">next flavor.</span>
          </h2>

          <p className="font-['Manrope'] text-base sm:text-xl text-black/80 max-w-2xl mx-auto font-medium">
            <span className="bg-black text-[#FFB300] px-2 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-wider">You</span> decide what we make next. The flavor with the most votes ships in our next batch. No marketing committee. No focus groups. Just you.
          </p>

          {/* Animated arrow */}
          {!userVote && (
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-flex items-center gap-2 mt-8 font-['Bebas_Neue'] text-xl sm:text-2xl text-black"
            >
              <Hand className="rotate-180" size={24} />
              Tap to cast your vote
              <Hand size={24} />
            </motion.div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {flavorOptions.map((flavor, index) => {
            const count = votes[flavor.id] || 0;
            const percent = getPercent(count);
            const isWinner = count === maxCount && total > 0;
            const isUserVote = userVote === flavor.id;

            return (
              <motion.button
                key={flavor.id}
                data-testid={`vote-button-${flavor.id}`}
                data-cursor={userVote ? '' : 'VOTE'}
                onClick={() => handleVote(flavor.id)}
                disabled={!!userVote || voting}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={!userVote ? { y: -10, rotate: -1, scale: 1.02 } : {}}
                className={`relative bg-black p-6 text-left border-4 transition-colors group ${
                  isUserVote ? 'border-[#FF3D00]' : 'border-black hover:border-[#FF3D00]'
                } ${userVote && !isUserVote ? 'opacity-60' : ''}`}
                style={{ cursor: userVote ? 'default' : 'pointer' }}
              >
                {/* Animated pulse glow for un-voted state */}
                {!userVote && (
                  <motion.div
                    aria-hidden
                    className="absolute -inset-1 bg-[#FF3D00] opacity-0 group-hover:opacity-30 blur-md pointer-events-none transition-opacity"
                    animate={{ opacity: [0, 0.15, 0] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: index * 0.3 }}
                  />
                )}

                {isUserVote && (
                  <div className="absolute -top-3 -right-3 bg-[#FF3D00] text-black px-2 py-1 font-['Bebas_Neue'] text-xs tracking-wider rotate-6 flex items-center gap-1">
                    <CheckCircle2 size={12} /> YOUR VOTE
                  </div>
                )}
                {isWinner && !isUserVote && (
                  <div className="absolute -top-3 -left-3 bg-[#FFB300] text-black px-2 py-1 font-['Bebas_Neue'] text-xs tracking-wider -rotate-6 border-2 border-black">
                    LEADING
                  </div>
                )}

                <div className="relative z-10">
                  <div className="text-5xl mb-3">{flavor.emoji}</div>
                  <h3 className="font-['Bebas_Neue'] text-3xl text-white uppercase tracking-tight mb-4">
                    {flavor.name}
                  </h3>

                  <div className="relative h-2 bg-white/10 mb-2 overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#FF3D00]"
                      initial={{ width: 0 }}
                      animate={inView ? { width: `${percent}%` } : {}}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1, ease: 'easeOut' }}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-['Bebas_Neue'] text-2xl text-[#FF3D00]">{percent}%</span>
                    <span className="font-['Manrope'] text-xs text-white/50">{count.toLocaleString()} votes</span>
                  </div>

                  {!userVote && (
                    <div className="mt-4 bg-[#FF3D00] text-black font-['Bebas_Neue'] text-base uppercase tracking-wider text-center py-2 group-hover:bg-[#FFB300] transition-colors">
                      Vote for {flavor.name}
                    </div>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>

        {userVote && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 text-center"
            data-testid="vote-confirmation"
          >
            <div className="inline-flex items-center gap-2 bg-black text-white px-6 py-3">
              <CheckCircle2 className="text-[#FF3D00]" size={18} />
              <p className="font-['Manrope'] text-sm sm:text-base">
                <span className="font-bold">Vote locked.</span> We'll announce the winner soon — keep an eye on{' '}
                <a href="https://instagram.com/go.waybig" target="_blank" rel="noopener noreferrer" className="text-[#FFB300] underline">@go.waybig</a>.
              </p>
            </div>
          </motion.div>
        )}

        {!userVote && (
          <p className="text-center mt-10 font-['Manrope'] text-sm text-black/60 max-w-xl mx-auto">
            One vote per person. Yes, we actually count these. Yes, the winner goes into production.
          </p>
        )}
      </div>
    </section>
  );
};

export default VoteSection;
